import { isUndefined } from 'lodash'
import uniqBy from 'lodash/uniqBy'
import { defineStore } from 'pinia'

import { Namespaces } from '@shared/models/enums/namespaces'
import { SocketChannels } from '@shared/models/enums/websocket-channels'
import { AnnouncementsChannelEvents } from '@shared/models/enums/websocket-events'
import { StoreState } from '@shared/models/interfaces/store'
import { IAnnouncementNotification } from '@shared/models/proto/base/announcement'
import { AnnouncementNotificationData } from '@shared/models/proto/base/decorators/announcement'
import { GetAnnouncementNotificationsDTO } from '@shared/models/proto/dto/announcement-notifications'
import { GetAnnouncementNotificationsRO } from '@shared/models/proto/ro/announcement-notifications'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { SessionProvider } from '@shared/plugins/session-provider'
import { PhoenixSocket } from '@shared/plugins/socket'
import { useAppsHeaderStore } from '@shared/store/apps-header'
import { INITIAL_PAGINATION } from '@shared/utils/constants/pagination'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { AnnouncementNotification } from '@protos/announcement_notification_pb'

interface State {
  notifications: StoreState<GetAnnouncementNotificationsRO>
}

const state = (): State => ({
  notifications: getInitialState([]),
})

export const useAnnouncementNotificationsStore = (namespace: Namespaces) =>
  defineStore({
    id: 'announcement-notifications',
    state,
    actions: {
      createAnnouncementChannel() {
        PhoenixSocket.findChannel(
          SessionProvider.chatUserId(),
          SocketChannels.AnnouncementNotificationChannel
        ).then(({ channel }) => {
          channel.on(AnnouncementsChannelEvents.Notification, async (json) => {
            await this.addNewNotification(json)
          })
        })
      },

      async getChannel() {
        const { channel } = await PhoenixSocket.findChannel(
          SessionProvider.chatUserId(),
          SocketChannels.AnnouncementNotificationChannel
        )

        return channel
      },

      async addNewNotification(notification: IAnnouncementNotification) {
        const headerStore = useAppsHeaderStore()

        const { data } = this.notifications.data ?? { data: [], meta: {} }

        if (
          !isUndefined(headerStore.counts.data?.announcementNotificationsCount)
        ) {
          headerStore.counts.data!.announcementNotificationsCount++
        }

        this.notifications = updateState(
          this.notifications,
          new GetAnnouncementNotificationsRO({
            data: [notification, ...(data ?? [])],
            meta: {
              ...this.meta,
              totalCount: this.meta.totalCount + 1,
            },
          })
        )
        try {
          const channel = await this.getChannel()

          channel.push(AnnouncementsChannelEvents.MarkDelivered, {
            announcement_uuid: notification.uuid,
          })
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Cant find the socket channel', e)
        }
      },

      async readNotification(notification: IAnnouncementNotification) {
        const headerStore = useAppsHeaderStore()
        const channel = await this.getChannel()
        const readAt = new Date().toISOString()

        const announcement_uuid = notification.uuid

        channel.push(AnnouncementsChannelEvents.MarkRead, {
          announcement_uuid,
        })

        const index = this.announcementList?.findIndex(
          (item: IAnnouncementNotification) => item.uuid === announcement_uuid
        )

        if (!isUndefined(index) && this.notifications.data) {
          const newObj = new AnnouncementNotification({
            ...this.notifications.data?.data[index],
            readAt,
          })

          Object.assign(this.notifications.data.data[index], newObj)

          if (headerStore.counts.data?.announcementNotificationsCount) {
            headerStore.counts.data.announcementNotificationsCount--
          }
        }
      },

      async fetchNotifications(
        payload: { page: number; perPage: number },
        isFetched = false
      ) {
        if (isFetched) {
          return
        }

        const apiTransport = new ApiTransport(resourcePath, namespace as string)

        try {
          const { data, meta } = (await apiTransport.get<
            GetAnnouncementNotificationsDTO,
            GetAnnouncementNotificationsRO
          >(
            '/announcement_notifications',
            new GetAnnouncementNotificationsDTO(payload),
            GetAnnouncementNotificationsRO
          )) ?? { data: [], meta: {} }

          const { data: currentNotificationsList = [] } =
            this.notifications.data ?? {}

          const updatedNotificationsList = [
            ...currentNotificationsList,
            ...data,
          ]

          const dataToSet = uniqBy(updatedNotificationsList, 'uuid')

          const list = {
            data: dataToSet,
            meta,
          }

          this.notifications = updateState(
            this.notifications,
            new GetAnnouncementNotificationsRO(list)
          )

          return list
        } catch (e) {
          this.notifications = updateState(
            this.notifications,
            new Error(`${e}`)
          )
          return Promise.reject(e)
        }
      },
    },
    getters: {
      meta: (state) => state.notifications?.data?.meta ?? INITIAL_PAGINATION,
      announcementList: (state) =>
        state.notifications?.data?.data?.map(
          (item: IAnnouncementNotification) =>
            new AnnouncementNotificationData(item)
        ),
    },
  })()
